import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './store/authStore'
import { useSystemStatus } from './hooks/useSystemStatus'
import LandingPage from './pages/LandingPage'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import ResetPassword from './pages/auth/ResetPassword'
import GalleryPage from './pages/GalleryPage'
import ShowcasePage from './pages/ShowcasePage'
import ShowcaseProjectPage from './pages/ShowcaseProjectPage'
import ContactPage from './pages/ContactPage'
import MasterclassPage from './pages/MasterclassPage'
import MasterclassArchivePage from './pages/masterclass/ArchivePage'
import MasterclassDetailPage from './pages/masterclass/DetailPage'
import MasterclassSpeakersPage from './pages/MasterclassSpeakersPage'
import SpeakerDetailPage from './pages/masterclass/SpeakerDetailPage'
import MasterclassGalleryPage from './pages/masterclass/GalleryPage'
import MasterclassVideosPage from './pages/masterclass/VideosPage'
import MasterclassResourcesPage from './pages/masterclass/ResourcesPage'
import VerifyCertificatePage from './pages/VerifyCertificatePage'
import CertificateOfAchievementPage from './pages/CertificateOfAchievementPage'
import MasterclassAdminPage from './pages/admin/MasterclassAdminPage'
import EventCheckInPage from './pages/admin/EventCheckInPage'
import StudentExperienceGateway from './pages/student/StudentExperienceGateway'
import MentorExperienceGateway from './pages/mentor/MentorExperienceGateway'
import StudentLibraryPage from './pages/student/StudentLibraryPage'
import TrainerDashboard from './pages/trainer/Dashboard'
import AdminDashboard from './pages/admin/Dashboard'
import FinanceDashboard from './pages/finance/Dashboard'
import InstallPrompt from './components/InstallPrompt'
import CourseSchema from './components/SEO/CourseSchema'
import WelcomePage from './pages/WelcomePage'
import AdvisorPage from './pages/AdvisorPage'
import ProgramsPage from './pages/ProgramsPage'
import HolidayProgramsPage from './pages/HolidayProgramsPage'
import HolidayProgramDetailPage from './pages/HolidayProgramDetailPage'
import IntakePage from './pages/IntakePage'
import ProjectLabPage from './pages/ProjectLabPage'
import ProjectLabDetailPage from './pages/ProjectLabDetailPage'
import CreativeToolsPage from './pages/CreativeToolsPage'
import CreativeToolPage from './pages/CreativeToolPage'
import Receipt from './pages/student/Receipt'
import { trackPageView, captureUtmParams } from './services/websiteTracker'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function AnalyticsTracker() {
  const { pathname, search } = useLocation()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    // Suppress analytics for authenticated users or portal paths
    if (isAuthenticated || /^\/(admin|finance|trainer|student|mentor)(\/|$)/i.test(pathname)) {
      return
    }

    captureUtmParams()
    // Small timeout to allow document.title / Helmet to update
    const timer = setTimeout(() => {
      trackPageView(pathname, document.title)
    }, 50)
    return () => clearTimeout(timer)
  }, [pathname, search, isAuthenticated])
  return null
}

function SpeakerRedirect() {
  const { id } = useParams()
  return <Navigate to={`/masterclass/speakers/${id}`} replace />
}

function App() {
  const { user, isAuthenticated, initializeAuth } = useAuthStore()
  useSystemStatus()

  useEffect(() => {
    // Initialize auth state from localStorage on app mount
    if (initializeAuth) {
      initializeAuth()
    }
  }, [initializeAuth])

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login')
      return <Navigate to="/login" replace />
    }

    if (allowedRoles && !allowedRoles.includes(user?.user_type)) {
      console.log('User role not allowed:', user?.user_type, 'Allowed:', allowedRoles)
      return <Navigate to="/" replace />
    }

    return children
  }

  const RedirectToDashboard = () => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }

    switch (user?.user_type) {
      case 'student':
        return <Navigate to="/student/dashboard" replace />
      case 'trainer':
        return <Navigate to="/trainer/dashboard" replace />
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />
      case 'finance':
        return <Navigate to="/finance/dashboard" replace />
      case 'project_lab_mentor':
        return <Navigate to="/mentor/dashboard" replace />
      default:
        return <Navigate to="/login" replace />
    }
  }

  const PublicOrRedirect = ({ children }) => {
    if (isAuthenticated) {
      switch (user?.user_type) {
        case 'student':
          return <Navigate to="/student/dashboard" replace />
        case 'trainer':
          return <Navigate to="/trainer/dashboard" replace />
        case 'admin':
          return <Navigate to="/admin/dashboard" replace />
        case 'finance':
          return <Navigate to="/finance/dashboard" replace />
        case 'project_lab_mentor':
          return <Navigate to="/mentor/dashboard" replace />
      }
    }
    return children
  }

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <AnalyticsTracker />
      <CourseSchema />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicOrRedirect><LandingPage /></PublicOrRedirect>} />
        <Route path="/explore" element={<PublicOrRedirect><LandingPage /></PublicOrRedirect>} />
        <Route path="/welcome" element={<PublicOrRedirect><WelcomePage /></PublicOrRedirect>} />
        <Route path="/advisor" element={<AdvisorPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/intake" element={<IntakePage />} />
        <Route path="/holiday-programs" element={<HolidayProgramsPage />} />
        <Route path="/holiday-programs/:courseCode" element={<HolidayProgramDetailPage />} />
        <Route path="/project-lab" element={<ProjectLabPage />} />
        <Route path="/project-lab/:slug" element={<ProjectLabDetailPage />} />
        <Route path="/creative-tools" element={<CreativeToolsPage />} />
        <Route path="/creative-tools/:slug" element={<CreativeToolPage />} />
        <Route path="/curriculum" element={<Navigate to="/programs" replace />} />
        <Route path="/courses" element={<Navigate to="/programs" replace />} />
        <Route path="/academy" element={<Navigate to="/explore#story" replace />} />
        <Route path="/mentors" element={<Navigate to="/explore#mentors" replace />} />
        <Route path="/faq" element={<Navigate to="/explore#faq" replace />} />
        <Route path="/login" element={<PublicOrRedirect><Login /></PublicOrRedirect>} />
        <Route path="/register" element={<PublicOrRedirect><Register /></PublicOrRedirect>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/showcase" element={<ShowcasePage />} />
        <Route path="/showcase/:slug" element={<ShowcaseProjectPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/masterclass" element={<MasterclassPage />} />
        <Route path="/masterclass/archive" element={<MasterclassArchivePage />} />
        <Route path="/masterclass/speakers" element={<MasterclassSpeakersPage />} />
        <Route path="/masterclass/speakers/:id" element={<SpeakerDetailPage />} />
        <Route path="/masterclass/gallery" element={<MasterclassGalleryPage />} />
        <Route path="/masterclass/videos" element={<MasterclassVideosPage />} />
        <Route path="/masterclass/resources" element={<MasterclassResourcesPage />} />
        <Route path="/masterclass/register" element={<Navigate to="/masterclass" replace />} />
        <Route
          path="/masterclass/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'trainer', 'finance']}>
              <MasterclassAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/event-check-in"
          element={
            <ProtectedRoute allowedRoles={['admin', 'trainer', 'finance']}>
              <EventCheckInPage />
            </ProtectedRoute>
          }
        />
        <Route path="/masterclass/:slug" element={<MasterclassDetailPage />} />
        <Route path="/speakers" element={<Navigate to="/masterclass/speakers" replace />} />
        <Route path="/speakers/:id" element={<SpeakerRedirect />} />
        <Route path="/gallery/masterclass" element={<Navigate to="/masterclass/gallery" replace />} />
        <Route path="/videos" element={<Navigate to="/masterclass/videos" replace />} />
        <Route path="/archive" element={<Navigate to="/masterclass/archive" replace />} />
        <Route path="/verify-certificate" element={<VerifyCertificatePage />} />
        <Route path="/verify" element={<VerifyCertificatePage />} />
        <Route path="/certificate-of-achievement" element={<CertificateOfAchievementPage />} />

        {/* Protected Routes */}
        <Route
          path="/student/*"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentExperienceGateway />
            </ProtectedRoute>
          }
        />

        <Route
          path="/trainer/*"
          element={
            <ProtectedRoute allowedRoles={['trainer']}>
              <TrainerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance/*"
          element={
            <ProtectedRoute allowedRoles={['finance', 'admin']}>
              <FinanceDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mentor/*"
          element={
            <ProtectedRoute allowedRoles={['project_lab_mentor']}>
              <MentorExperienceGateway />
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance/receipts/:paymentId"
          element={
            <ProtectedRoute allowedRoles={['finance', 'admin']}>
              <Receipt />
            </ProtectedRoute>
          }
        />

        <Route
          path="/receipts/:paymentId"
          element={
            <ProtectedRoute allowedRoles={['student', 'trainer', 'admin', 'finance']}>
              <Receipt />
            </ProtectedRoute>
          }
        />

        {/* Dashboard shortcut for logged-in users */}
        <Route path="/dashboard" element={<RedirectToDashboard />} />

        {/* Default Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <InstallPrompt />
    </div>
  )
}

export default App
